import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import {
  MessageSquare,
  Search,
  Send,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Bell,
  Inbox,
  Clock3,
  ChevronRight,
  X,
  Trash2,
  ArrowDown,
  FolderKanban,
} from "lucide-react";
import "./LenderCommunicationPage.css";
import {
  fetchCommunicationThreads,
  fetchCommunicationThreadDetail,
  createThreadMessage,
  fetchCommunicationNotificationsSummary,
  markThreadAsRead,
  markAllNotificationsAsRead,
  archiveCommunicationThread,
  deleteThreadMessage,
} from "../../homebuyer/communication/communicationApi";

function formatDateTime(value, locale = "en-GB") {
  if (!value) return "";
  try {
    const date = new Date(value);
    return date.toLocaleString(locale, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function formatTime(value, locale = "en-GB") {
  if (!value) return "";
  try {
    return new Date(value).toLocaleTimeString(locale, {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function getParticipantDisplay(participant, t) {
  if (!participant) return t("common.unknown", "Unknown");

  if (participant.display_name) return participant.display_name;

  const first = participant.user_first_name || "";
  const last = participant.user_last_name || "";
  const fullName = `${first} ${last}`.trim();

  if (fullName) return fullName;
  if (participant.organisation_name) return participant.organisation_name;
  if (participant.user_email) return participant.user_email;

  return t(`roles.${(participant.role || "").toLowerCase()}`, participant.role);
}

function getMessageParticipant(message, thread) {
  if (!message || !thread) return null;

  const senderId = message.sender ?? message.sender_id;

  return (
    (thread.participants || []).find(
      (participant) => String(participant.id) === String(senderId),
    ) ||
    (thread.participants || []).find(
      (participant) => participant.role === message.sender_role,
    ) ||
    null
  );
}

function getParticipantInitials(participant) {
  if (!participant) return "?";

  const firstName = participant.user_first_name || "";
  const lastName = participant.user_last_name || "";

  const userInitials =
    `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();

  if (userInitials) {
    return userInitials;
  }

  const organisationName = participant.organisation_name || "";

  if (organisationName) {
    return organisationName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => word.charAt(0))
      .join("")
      .toUpperCase();
  }

  return (
    participant.user_email?.charAt(0)?.toUpperCase() ||
    participant.role?.charAt(0)?.toUpperCase() ||
    "?"
  );
}

function ParticipantAvatar({
  participant,
  fallbackName = "",
  size = "message",
}) {
  const displayName =
    participant?.display_name ||
    participant?.organisation_name ||
    participant?.user_email ||
    fallbackName ||
    participant?.role ||
    "Participant";

  const fallbackInitials =
    displayName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0))
      .join("")
      .toUpperCase() || "?";

  return (
    <div
      className={`lender-comm-avatar lender-comm-avatar--${size}`}
      title={displayName}
      aria-label={displayName}
    >
      {participant?.avatar_url ? (
        <img
          src={participant.avatar_url}
          alt={displayName}
          className="lender-comm-avatar__image"
          onError={(event) => {
            event.currentTarget.style.display = "none";
          }}
        />
      ) : (
        <span className="lender-comm-avatar__fallback">{fallbackInitials}</span>
      )}
    </div>
  );
}

function getCounterpartParticipants(thread) {
  const participants = thread?.participants || [];
  const seen = new Set();

  return participants.filter((participant) => {
    if (!participant.is_active) {
      return false;
    }

    if (participant.role === "LENDER") {
      return false;
    }

    const uniqueKey =
      participant.organisation ||
      participant.user ||
      participant.organisation_name ||
      participant.user_email ||
      `${participant.role}-${participant.id}`;

    if (seen.has(uniqueKey)) {
      return false;
    }

    seen.add(uniqueKey);
    return true;
  });
}

function getThreadOtherParticipants(thread, t) {
  return getCounterpartParticipants(thread)
    .map((participant) => getParticipantDisplay(participant, t))
    .join(", ");
}

function isOwnMessage(message, selectedThread) {
  if (
    !message ||
    !selectedThread ||
    selectedThread.viewer_participant_id == null
  ) {
    return false;
  }

  const senderId = message.sender ?? message.sender_id;

  return String(senderId) === String(selectedThread.viewer_participant_id);
}

function isSameMinute(a, b) {
  if (!a || !b) return false;
  const da = new Date(a);
  const db = new Date(b);

  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate() &&
    da.getHours() === db.getHours() &&
    da.getMinutes() === db.getMinutes()
  );
}

function buildMessageGroups(messages, selectedThread) {
  const groups = [];

  messages.forEach((message) => {
    const mine = isOwnMessage(message, selectedThread);

    const senderParticipant = getMessageParticipant(message, selectedThread);

    const lastGroup = groups[groups.length - 1];

    const canAppend =
      lastGroup &&
      lastGroup.isMine === mine &&
      lastGroup.senderId === message.sender &&
      isSameMinute(
        lastGroup.items[lastGroup.items.length - 1]?.created_at,
        message.created_at,
      );

    if (canAppend) {
      lastGroup.items.push(message);
    } else {
      groups.push({
        senderId: message.sender,
        senderParticipant,
        isMine: mine,
        items: [message],
      });
    }
  });

  return groups;
}

function getThreadGroupKey(thread) {
  if (thread.loan_case_id) {
    return `loan-case-${thread.loan_case_id}`;
  }

  return `thread-${thread.id}`;
}

function getThreadGroupLabel(thread, t) {
  if (thread.loan_case_reference) {
    return thread.loan_case_reference;
  }

  if (thread.loan_case_id) {
    return `${t("groups.loanCase", "Loan case")} #${thread.loan_case_id}`;
  }

  return t("groups.other", "Other conversations");
}

function getThreadActorLabel(thread, t) {
  switch (thread?.channel_scope) {
    case "LENDER_HOMEBUYER":
      return t("actors.homebuyer", "Homebuyer");

    case "LENDER_OSS":
      return t("actors.oss", "One-Stop Shop");

    default:
      return t("actors.general", "General");
  }
}

function getThreadActorClass(thread) {
  switch (thread?.channel_scope) {
    case "LENDER_HOMEBUYER":
      return "is-homebuyer";

    case "LENDER_OSS":
      return "is-oss";

    default:
      return "is-general";
  }
}

function getThreadActorOrder(thread) {
  switch (thread?.channel_scope) {
    case "LENDER_HOMEBUYER":
      return 1;

    case "LENDER_OSS":
      return 2;

    default:
      return 3;
  }
}

export default function LenderCommunicationPage() {
  const { t, i18n } = useTranslation("lender-communication");
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  const [searchParams, setSearchParams] = useSearchParams();

  const requestedThreadId = React.useMemo(() => {
    const rawThreadId = searchParams.get("thread");

    if (!rawThreadId) return null;

    const parsedThreadId = Number(rawThreadId);

    return Number.isInteger(parsedThreadId) && parsedThreadId > 0
      ? parsedThreadId
      : null;
  }, [searchParams]);

  const [threads, setThreads] = useState([]);
  const [selectedThreadId, setSelectedThreadId] = useState(null);
  const [selectedThread, setSelectedThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [notificationsUnread, setNotificationsUnread] = useState(0);

  const [searchTerm, setSearchTerm] = useState("");
  const [messageBody, setMessageBody] = useState("");

  const [isLoadingThreads, setIsLoadingThreads] = useState(true);
  const [isLoadingThreadDetail, setIsLoadingThreadDetail] = useState(false);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [isMarkingAllRead, setIsMarkingAllRead] = useState(false);

  const [deleteModal, setDeleteModal] = useState({
    open: false,
    threadId: null,
    title: "",
  });

  const [messageDeleteModal, setMessageDeleteModal] = useState({
    open: false,
    messageId: null,
  });

  const [isDeletingMessage, setIsDeletingMessage] = useState(false);

  const [feedbackModal, setFeedbackModal] = useState({
    open: false,
    type: "",
    title: "",
    text: "",
  });

  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [lastMessageId, setLastMessageId] = useState(null);
  const [firstUnreadMessageId, setFirstUnreadMessageId] = useState(null);

  const messagesContainerRef = useRef(null);
  const stayReadTimerRef = useRef(null);
  const scrollReadTimerRef = useRef(null);
  const isMarkingThreadReadRef = useRef(false);
  const previousSelectedThreadIdRef = useRef(null);

  const locale =
    i18n.language === "es"
      ? "es-ES"
      : i18n.language === "ca"
        ? "ca-ES"
        : i18n.language === "nl"
          ? "nl-BE"
          : "en-GB";

  const selectedThreadUnreadCount =
    threads.find((thread) => thread.id === selectedThreadId)?.unread_count || 0;

  const closeFeedbackModal = () => {
    setFeedbackModal({
      open: false,
      type: "",
      title: "",
      text: "",
    });
  };

  function handleSelectThread(threadId, options = {}) {
    const { replace = false } = options;

    const numericThreadId = Number(threadId);

    if (!Number.isInteger(numericThreadId)) {
      return;
    }

    setSelectedThreadId(numericThreadId);

    const nextSearchParams = new URLSearchParams(searchParams);

    nextSearchParams.set("thread", String(numericThreadId));

    setSearchParams(nextSearchParams, {
      replace,
    });
  }

  async function getAccessToken() {
    return getAccessTokenSilently();
  }

  function isNearBottom(container) {
    if (!container) return false;
    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    return distanceFromBottom <= 120;
  }

  function scrollToBottom(behavior = "smooth") {
    const container = messagesContainerRef.current;
    if (!container) return;

    container.scrollTo({
      top: container.scrollHeight,
      behavior,
    });
  }

  async function loadNotificationsSummary() {
    try {
      const accessToken = await getAccessToken();
      const data = await fetchCommunicationNotificationsSummary({
        accessToken,
      });
      setNotificationsUnread(data?.unread_count || 0);
    } catch {
      // silent
    }
  }

  async function loadThreads(options = {}) {
    const { silent = false } = options;

    try {
      if (!silent) setIsLoadingThreads(true);

      const accessToken = await getAccessToken();
      const data = await fetchCommunicationThreads({ accessToken });

      const nextThreads = Array.isArray(data) ? data : [];
      setThreads(nextThreads);

      if (nextThreads.length > 0) {
        setSelectedThreadId((previousThreadId) => {
          const requestedThreadExists =
            requestedThreadId &&
            nextThreads.some(
              (thread) => Number(thread.id) === requestedThreadId,
            );

          if (requestedThreadExists) {
            return requestedThreadId;
          }

          const previousThreadExists =
            previousThreadId &&
            nextThreads.some(
              (thread) => Number(thread.id) === Number(previousThreadId),
            );

          if (previousThreadExists) {
            return previousThreadId;
          }

          return nextThreads[0].id;
        });
      } else {
        setSelectedThreadId(null);
        setSelectedThread(null);
        setMessages([]);
        setFirstUnreadMessageId(null);
        setShowScrollToBottom(false);
      }

      await loadNotificationsSummary();
    } catch (error) {
      if (!silent) {
        setFeedbackModal({
          open: true,
          type: "error",
          title: t("messages.errorTitle", "Something went wrong"),
          text:
            error.message ||
            t(
              "messages.loadThreadsError",
              "Could not load communication threads.",
            ),
        });
      }
    } finally {
      if (!silent) setIsLoadingThreads(false);
    }
  }

  async function loadThreadDetail(threadId, options = {}) {
    if (!threadId) return;

    const { silent = false } = options;

    try {
      if (!silent) setIsLoadingThreadDetail(true);

      const container = messagesContainerRef.current;
      const wasNearBottom = isNearBottom(container);
      const previousScrollTop = container ? container.scrollTop : 0;
      const previousScrollHeight = container ? container.scrollHeight : 0;

      const currentThread = threads.find((thread) => thread.id === threadId);
      const unreadCount = currentThread?.unread_count || 0;

      const accessToken = await getAccessToken();
      const data = await fetchCommunicationThreadDetail({
        accessToken,
        threadId,
      });

      const nextMessages = Array.isArray(data?.messages) ? data.messages : [];
      const nextLastMessageId =
        nextMessages.length > 0
          ? nextMessages[nextMessages.length - 1].id
          : null;

      const hadMessagesBefore = messages.length > 0;
      const hasNewMessage =
        hadMessagesBefore &&
        nextLastMessageId &&
        lastMessageId &&
        nextLastMessageId !== lastMessageId;

      let firstUnreadId = null;
      if (unreadCount > 0 && nextMessages.length >= unreadCount) {
        const unreadSlice = nextMessages.slice(-unreadCount);
        firstUnreadId = unreadSlice[0]?.id || null;
      }

      setSelectedThread(data);
      setMessages(nextMessages);
      setLastMessageId(nextLastMessageId);
      setFirstUnreadMessageId(firstUnreadId);

      requestAnimationFrame(() => {
        const currentContainer = messagesContainerRef.current;
        if (!currentContainer) return;

        if (!hadMessagesBefore) {
          currentContainer.scrollTop = 0;
          setShowScrollToBottom(
            unreadCount > 0 || !isNearBottom(currentContainer),
          );
          return;
        }

        if (wasNearBottom) {
          currentContainer.scrollTop = currentContainer.scrollHeight;
          setShowScrollToBottom(false);
          return;
        }

        const newScrollHeight = currentContainer.scrollHeight;
        const heightDiff = newScrollHeight - previousScrollHeight;
        currentContainer.scrollTop = previousScrollTop + heightDiff;

        if (hasNewMessage) {
          setShowScrollToBottom(true);
        } else {
          setShowScrollToBottom(!isNearBottom(currentContainer));
        }
      });
    } catch (error) {
      if (!silent) {
        setFeedbackModal({
          open: true,
          type: "error",
          title: t("messages.errorTitle", "Something went wrong"),
          text:
            error.message ||
            t(
              "messages.loadThreadError",
              "Could not load the selected thread.",
            ),
        });
      }
    } finally {
      if (!silent) setIsLoadingThreadDetail(false);
    }
  }

  async function handleMarkSelectedThreadAsRead(trigger = "stay") {
    if (!selectedThreadId || !selectedThread) return;
    if (isMarkingThreadReadRef.current) return;

    const currentThread = threads.find(
      (thread) => thread.id === selectedThreadId,
    );
    const unreadCount = currentThread?.unread_count || 0;

    if (unreadCount <= 0) return;

    const container = messagesContainerRef.current;
    if (!isNearBottom(container)) return;

    try {
      isMarkingThreadReadRef.current = true;

      const accessToken = await getAccessToken();
      await markThreadAsRead({ accessToken, threadId: selectedThreadId });

      setThreads((prev) =>
        prev.map((thread) =>
          thread.id === selectedThreadId
            ? { ...thread, unread_count: 0 }
            : thread,
        ),
      );

      setFirstUnreadMessageId(null);
      setShowScrollToBottom(false);
      await loadNotificationsSummary();
    } catch (error) {
      console.error(`Failed to mark thread as read via ${trigger}:`, error);
    } finally {
      isMarkingThreadReadRef.current = false;
    }
  }

  useEffect(() => {
    if (!isAuthenticated) return;
    loadThreads();
  }, [isAuthenticated]);

  useEffect(() => {
    if (!requestedThreadId || threads.length === 0) {
      return;
    }

    const requestedThreadExists = threads.some(
      (thread) => Number(thread.id) === requestedThreadId,
    );

    if (
      requestedThreadExists &&
      Number(selectedThreadId) !== requestedThreadId
    ) {
      setSelectedThreadId(requestedThreadId);
    }
  }, [requestedThreadId, threads, selectedThreadId]);

  useEffect(() => {
    if (!selectedThreadId || !isAuthenticated) return;

    if (previousSelectedThreadIdRef.current !== selectedThreadId) {
      previousSelectedThreadIdRef.current = selectedThreadId;
      setShowScrollToBottom(false);
      setFirstUnreadMessageId(null);
    }

    if (stayReadTimerRef.current) clearTimeout(stayReadTimerRef.current);
    if (scrollReadTimerRef.current) clearTimeout(scrollReadTimerRef.current);

    loadThreadDetail(selectedThreadId);

    return () => {
      if (stayReadTimerRef.current) clearTimeout(stayReadTimerRef.current);
      if (scrollReadTimerRef.current) clearTimeout(scrollReadTimerRef.current);
    };
  }, [selectedThreadId, isAuthenticated]);

  useEffect(() => {
    if (!selectedThreadId || !selectedThread || isLoadingThreadDetail) return;
    if (selectedThreadUnreadCount <= 0) return;

    if (stayReadTimerRef.current) clearTimeout(stayReadTimerRef.current);

    stayReadTimerRef.current = setTimeout(() => {
      handleMarkSelectedThreadAsRead("stay");
    }, 5500);

    return () => {
      if (stayReadTimerRef.current) clearTimeout(stayReadTimerRef.current);
    };
  }, [
    selectedThreadId,
    selectedThreadUnreadCount,
    selectedThread,
    isLoadingThreadDetail,
  ]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container || !selectedThreadId || !selectedThread) return;

    function handleMessagesScroll() {
      const nearBottom = isNearBottom(container);

      setShowScrollToBottom(!nearBottom);

      if (!nearBottom) return;
      if (selectedThreadUnreadCount <= 0) return;

      if (scrollReadTimerRef.current) clearTimeout(scrollReadTimerRef.current);

      scrollReadTimerRef.current = setTimeout(() => {
        handleMarkSelectedThreadAsRead("scroll");
      }, 2500);
    }

    container.addEventListener("scroll", handleMessagesScroll, {
      passive: true,
    });
    handleMessagesScroll();

    return () => {
      container.removeEventListener("scroll", handleMessagesScroll);
      if (scrollReadTimerRef.current) clearTimeout(scrollReadTimerRef.current);
    };
  }, [selectedThreadId, selectedThread, selectedThreadUnreadCount]);

  const filteredThreads = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    if (!term) {
      return threads;
    }

    const isNumericOnlySearch = /^\d+$/.test(term);

    return threads.filter((thread) => {
      const loanCaseReference = (
        thread.loan_case_reference || ""
      ).toLowerCase();

      const loanCaseId = String(thread.loan_case_id || "");

      const referenceSuffix = loanCaseReference.includes("-")
        ? loanCaseReference.split("-").pop() || ""
        : loanCaseReference;

      const participantText = getThreadOtherParticipants(
        thread,
        t,
      ).toLowerCase();

      const titleText = (thread.title || "").toLowerCase();

      const latestMessageText = (
        thread.latest_message?.body || ""
      ).toLowerCase();

      if (isNumericOnlySearch) {
        return loanCaseId.includes(term) || referenceSuffix.includes(term);
      }

      return (
        loanCaseReference.includes(term) ||
        loanCaseId.includes(term) ||
        participantText.includes(term) ||
        titleText.includes(term) ||
        latestMessageText.includes(term)
      );
    });
  }, [threads, searchTerm, t]);

  const groupedThreads = useMemo(() => {
    const groupsMap = new Map();

    filteredThreads.forEach((thread) => {
      const key = getThreadGroupKey(thread);
      const label = getThreadGroupLabel(thread, t);

      if (!groupsMap.has(key)) {
        groupsMap.set(key, {
          key,
          label,
          loanCaseId: thread.loan_case_id || null,
          loanCaseReference: thread.loan_case_reference || null,
          items: [],
          unreadCount: 0,
        });
      }

      const group = groupsMap.get(key);
      group.items.push(thread);
      group.unreadCount += thread.unread_count || 0;
    });

    return Array.from(groupsMap.values())
      .map((group) => ({
        ...group,
        items: [...group.items].sort((a, b) => {
          const roleDiff = getThreadActorOrder(a) - getThreadActorOrder(b);
          if (roleDiff !== 0) return roleDiff;

          const aDate = new Date(
            a.latest_message?.created_at || a.updated_at || 0,
          ).getTime();
          const bDate = new Date(
            b.latest_message?.created_at || b.updated_at || 0,
          ).getTime();

          return bDate - aDate;
        }),
      }))
      .sort((a, b) => {
        const aLatest = Math.max(
          ...a.items.map((item) =>
            new Date(
              item.latest_message?.created_at || item.updated_at || 0,
            ).getTime(),
          ),
        );
        const bLatest = Math.max(
          ...b.items.map((item) =>
            new Date(
              item.latest_message?.created_at || item.updated_at || 0,
            ).getTime(),
          ),
        );

        return bLatest - aLatest;
      });
  }, [filteredThreads, t]);

  async function handleSendMessage(event) {
    event.preventDefault();

    const trimmed = messageBody.trim();
    if (!trimmed || !selectedThreadId) return;

    try {
      setIsSendingMessage(true);

      const accessToken = await getAccessToken();

      await createThreadMessage({
        accessToken,
        threadId: selectedThreadId,
        body: trimmed,
        messageType: "COMMENT",
        visibility: "ALL",
      });

      setMessageBody("");
      await loadThreadDetail(selectedThreadId, { silent: true });
      await loadThreads({ silent: true });
      setShowScrollToBottom(true);
    } catch (error) {
      setFeedbackModal({
        open: true,
        type: "error",
        title: t("messages.errorTitle", "Something went wrong"),
        text:
          error.message ||
          t("messages.sendError", "Could not send your message."),
      });
    } finally {
      setIsSendingMessage(false);
    }
  }

  function handleComposerKeyDown(event) {
    const isSubmitShortcut =
      event.key === "Enter" && (event.metaKey || event.ctrlKey);

    if (!isSubmitShortcut) return;

    if (event.nativeEvent?.isComposing) return;

    event.preventDefault();

    if (!messageBody.trim() || isSendingMessage) return;

    handleSendMessage(event);
  }

  async function handleMarkAllNotificationsRead() {
    try {
      setIsMarkingAllRead(true);

      const accessToken = await getAccessToken();
      await markAllNotificationsAsRead({ accessToken });

      setNotificationsUnread(0);
      setThreads((prev) =>
        prev.map((thread) => ({
          ...thread,
          unread_count: 0,
        })),
      );
      setFirstUnreadMessageId(null);
    } catch (error) {
      setFeedbackModal({
        open: true,
        type: "error",
        title: t("messages.errorTitle", "Something went wrong"),
        text:
          error.message ||
          t(
            "messages.markAllReadError",
            "Could not mark notifications as read.",
          ),
      });
    } finally {
      setIsMarkingAllRead(false);
    }
  }

  async function confirmDeleteThread() {
    if (!deleteModal.threadId) return;

    try {
      const accessToken = await getAccessToken();

      await archiveCommunicationThread({
        accessToken,
        threadId: deleteModal.threadId,
      });

      const updatedThreads = threads.filter(
        (thread) => thread.id !== deleteModal.threadId,
      );

      setThreads(updatedThreads);

      if (Number(selectedThreadId) === Number(deleteModal.threadId)) {
        if (updatedThreads.length > 0) {
          handleSelectThread(updatedThreads[0].id, { replace: true });
        } else {
          setSelectedThreadId(null);
          setSelectedThread(null);
          setMessages([]);

          const nextSearchParams = new URLSearchParams(searchParams);

          nextSearchParams.delete("thread");

          setSearchParams(nextSearchParams, {
            replace: true,
          });
        }
      }

      setDeleteModal({
        open: false,
        threadId: null,
        title: "",
      });

      setFeedbackModal({
        open: true,
        type: "success",
        title: t("messages.successTitle", "Success"),
        text: t(
          "messages.threadDeleted",
          "The conversation was deleted successfully.",
        ),
      });

      await loadNotificationsSummary();
    } catch (error) {
      setDeleteModal({
        open: false,
        threadId: null,
        title: "",
      });

      setFeedbackModal({
        open: true,
        type: "error",
        title: t("messages.errorTitle", "Something went wrong"),
        text:
          error.message ||
          t("messages.deleteThreadError", "Could not delete the conversation."),
      });
    }
  }

  async function confirmDeleteMessage() {
    if (!messageDeleteModal.messageId || !selectedThreadId) {
      return;
    }

    try {
      setIsDeletingMessage(true);

      const accessToken = await getAccessToken();

      await deleteThreadMessage({
        accessToken,
        threadId: selectedThreadId,
        messageId: messageDeleteModal.messageId,
      });

      setMessageDeleteModal({
        open: false,
        messageId: null,
      });

      await loadThreadDetail(selectedThreadId, {
        silent: true,
      });

      await loadThreads({
        silent: true,
      });
    } catch (error) {
      setFeedbackModal({
        open: true,
        type: "error",
        title: t("messages.errorTitle", "Something went wrong"),
        text:
          error.message ||
          t("messages.deleteMessageError", "The message could not be deleted."),
      });
    } finally {
      setIsDeletingMessage(false);
    }
  }

  useEffect(() => {
    if (!selectedThreadId || !isAuthenticated) return;

    const interval = setInterval(async () => {
      if (document.visibilityState !== "visible") return;

      try {
        await loadThreadDetail(selectedThreadId, { silent: true });
        await loadThreads({ silent: true });
      } catch (error) {
        console.error("Polling communication thread failed:", error);
      }
    }, 15000);

    return () => clearInterval(interval);
  }, [selectedThreadId, isAuthenticated]);

  function handleScrollToUnreadOrBottom() {
    if (firstUnreadMessageId) {
      const target = document.getElementById(
        `lender-comm-message-anchor-${firstUnreadMessageId}`,
      );
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
    }

    scrollToBottom("smooth");
  }

  useEffect(() => {
    if (!selectedThreadId || requestedThreadId) {
      return;
    }

    const nextSearchParams = new URLSearchParams(searchParams);

    nextSearchParams.set("thread", String(selectedThreadId));

    setSearchParams(nextSearchParams, {
      replace: true,
    });
  }, [selectedThreadId, requestedThreadId, searchParams, setSearchParams]);

  return (
    <>
      <div className="lender-comm-page">
        <div className="lender-comm-page__shell">
          <header className="lender-comm-hero">
            <div>
              <p className="lender-comm-hero__eyebrow">
                {t("hero.eyebrow", "Communication hub")}
              </p>
              <h1 className="lender-comm-hero__title">
                {t("hero.title", "Messages & coordination")}
              </h1>
              <p className="lender-comm-hero__subtitle">
                {t(
                  "hero.subtitle",
                  "Follow conversations across loan cases, homeowners and One-Stop Shops in one place.",
                )}
              </p>
            </div>

            <div className="lender-comm-hero__meta">
              <div className="lender-comm-hero__meta-card">
                <span>{t("hero.totalThreads", "Threads")}</span>
                <strong>{threads.length}</strong>
              </div>

              <div className="lender-comm-hero__meta-card">
                <span>{t("hero.unreadNotifications", "Unread")}</span>
                <strong>{notificationsUnread}</strong>
              </div>

              <button
                type="button"
                className="lender-comm-hero__meta-btn"
                onClick={handleMarkAllNotificationsRead}
                disabled={isMarkingAllRead || notificationsUnread === 0}
              >
                <Bell size={15} />
                {isMarkingAllRead
                  ? t("buttons.marking", "Marking...")
                  : t("buttons.markAllRead", "Mark all as read")}
              </button>
            </div>
          </header>

          <div className="lender-comm-layout">
            <aside className="lender-comm-sidebar">
              <div className="lender-comm-sidebar__top">
                <div className="lender-comm-search">
                  <Search size={16} />
                  <input
                    type="text"
                    placeholder={t(
                      "search.placeholder",
                      "Search conversations...",
                    )}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              <div className="lender-comm-sidebar__list">
                {isLoadingThreads ? (
                  <div className="lender-comm-empty">
                    <Loader2 size={18} className="lender-comm-spin" />
                    <span>
                      {t("messages.loadingThreads", "Loading threads...")}
                    </span>
                  </div>
                ) : groupedThreads.length === 0 ? (
                  <div className="lender-comm-empty">
                    <Inbox size={18} />
                    <span>
                      {t(
                        "messages.noThreads",
                        "No communication threads found.",
                      )}
                    </span>
                  </div>
                ) : (
                  groupedThreads.map((group) => (
                    <div className="lender-comm-group" key={group.key}>
                      <div className="lender-comm-group__header">
                        <div className="lender-comm-group__title-wrap">
                          <FolderKanban size={14} />
                          <div className="lender-comm-group__title-block">
                            <span className="lender-comm-group__title">
                              {group.label}
                            </span>
                            <span className="lender-comm-group__subtitle">
                              {group.items.length}{" "}
                              {group.items.length === 1
                                ? t("groups.threadSingle", "conversation")
                                : t("groups.threadPlural", "conversations")}
                            </span>
                          </div>
                        </div>

                        {group.unreadCount > 0 ? (
                          <span className="lender-comm-group__badge">
                            {group.unreadCount}
                          </span>
                        ) : null}
                      </div>

                      <div className="lender-comm-group__items">
                        {group.items.map((thread, index) => {
                          const isSelected = selectedThreadId === thread.id;
                          const actorLabel = getThreadActorLabel(thread, t);
                          const actorClass = getThreadActorClass(thread);

                          return (
                            <React.Fragment key={thread.id}>
                              <button
                                type="button"
                                className={`lender-comm-thread-card ${
                                  isSelected ? "is-selected" : ""
                                }`}
                                onClick={() => handleSelectThread(thread.id)}
                              >
                                <div className="lender-comm-thread-card__top">
                                  <div>
                                    <div className="lender-comm-thread-card__title-row">
                                      <span
                                        className={`lender-comm-thread-card__actor-badge ${actorClass}`}
                                      >
                                        {actorLabel}
                                      </span>

                                      {thread.unread_count > 0 ? (
                                        <span className="lender-comm-thread-card__badge">
                                          {thread.unread_count}
                                        </span>
                                      ) : null}
                                    </div>

                                    <h3 className="lender-comm-thread-card__title">
                                      {thread.title}
                                    </h3>

                                    <p className="lender-comm-thread-card__type">
                                      {t(
                                        `threadTypes.${thread.thread_type}`,
                                        thread.thread_type,
                                      )}
                                    </p>
                                  </div>

                                  <ChevronRight size={16} />
                                </div>

                                <p className="lender-comm-thread-card__participants">
                                  {getThreadOtherParticipants(thread, t) ||
                                    t(
                                      "common.noParticipants",
                                      "No participants",
                                    )}
                                </p>

                                <p className="lender-comm-thread-card__preview">
                                  {thread.latest_message?.body ||
                                    t(
                                      "common.noMessagesYet",
                                      "No messages yet.",
                                    )}
                                </p>

                                <div className="lender-comm-thread-card__bottom">
                                  <span className="lender-comm-thread-card__status">
                                    {t(
                                      `statuses.${thread.status}`,
                                      thread.status || "OPEN",
                                    )}
                                  </span>

                                  <span className="lender-comm-thread-card__time">
                                    <Clock3 size={12} />
                                    {formatDateTime(
                                      thread.latest_message?.created_at ||
                                        thread.updated_at,
                                      locale,
                                    )}
                                  </span>
                                </div>
                              </button>

                              {index < group.items.length - 1 ? (
                                <div className="lender-comm-group__divider" />
                              ) : null}
                            </React.Fragment>
                          );
                        })}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </aside>

            <section className="lender-comm-main">
              {!selectedThreadId ? (
                <div className="lender-comm-main__empty">
                  <MessageSquare size={28} />
                  <h2>{t("empty.title", "Select a conversation")}</h2>
                  <p>
                    {t(
                      "empty.text",
                      "Choose a thread from the left to view messages and continue the discussion.",
                    )}
                  </p>
                </div>
              ) : isLoadingThreadDetail ? (
                <div className="lender-comm-main__empty">
                  <Loader2 size={22} className="lender-comm-spin" />
                  <h2>
                    {t("messages.loadingThread", "Loading conversation...")}
                  </h2>
                </div>
              ) : (
                <>
                  <div className="lender-comm-thread-header">
                    <div className="lender-comm-thread-header__top">
                      <div className="lender-comm-thread-header__title-wrap">
                        <div className="lender-comm-thread-header__title-row">
                          <h2 className="lender-comm-thread-header__title">
                            {selectedThread?.title ||
                              t("common.thread", "Thread")}
                          </h2>

                          {selectedThreadUnreadCount > 0 ? (
                            <span className="lender-comm-thread-header__unread-badge">
                              {selectedThreadUnreadCount}
                            </span>
                          ) : null}
                        </div>

                        <div className="lender-comm-thread-header__meta">
                          {selectedThread?.loan_case_reference ? (
                            <>
                              <span>{selectedThread.loan_case_reference}</span>
                              <span>•</span>
                            </>
                          ) : selectedThread?.loan_case_id ? (
                            <>
                              <span>
                                {t(
                                  "groups.loanCaseWithId",
                                  "Loan case #{{id}}",
                                  {
                                    id: selectedThread.loan_case_id,
                                  },
                                )}
                              </span>
                              <span>•</span>
                            </>
                          ) : null}

                          <span>{getThreadActorLabel(selectedThread, t)}</span>
                          <span>•</span>

                          <span>
                            {t(
                              `threadTypes.${selectedThread?.thread_type}`,
                              selectedThread?.thread_type || "GENERAL",
                            )}
                          </span>
                          <span>•</span>
                          <span>
                            {t(
                              `statuses.${selectedThread?.status}`,
                              selectedThread?.status || "OPEN",
                            )}
                          </span>
                        </div>
                      </div>

                      <button
                        type="button"
                        className="lender-comm-delete-icon-btn"
                        onClick={() =>
                          setDeleteModal({
                            open: true,
                            threadId: selectedThread?.id || null,
                            title: selectedThread?.title || "",
                          })
                        }
                        title={t("buttons.deleteThread", "Delete conversation")}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>

                    <div className="lender-comm-thread-header__participants">
                      {getCounterpartParticipants(selectedThread).map(
                        (participant) => (
                          <div
                            className="lender-comm-thread-header__participant"
                            key={participant.id}
                          >
                            <ParticipantAvatar
                              participant={participant}
                              size="header"
                            />

                            <div className="lender-comm-thread-header__participant-info">
                              <span className="lender-comm-thread-header__participant-label">
                                {selectedThread?.channel_scope === "LENDER_OSS"
                                  ? t("actors.oss", "One-Stop Shop")
                                  : t("actors.homebuyer", "Homebuyer")}
                              </span>

                              <strong className="lender-comm-thread-header__participant-name">
                                {getParticipantDisplay(participant, t)}
                              </strong>
                            </div>
                          </div>
                        ),
                      )}
                    </div>
                  </div>

                  <div className="lender-comm-messages-wrapper">
                    <div
                      className="lender-comm-messages"
                      ref={messagesContainerRef}
                    >
                      {messages.length === 0 ? (
                        <div className="lender-comm-main__empty lender-comm-main__empty--messages">
                          <Inbox size={22} />
                          <h3>{t("messages.noMessages", "No messages yet")}</h3>
                          <p>
                            {t(
                              "messages.noMessagesText",
                              "Start the conversation by sending your first message below.",
                            )}
                          </p>
                        </div>
                      ) : (
                        buildMessageGroups(messages, selectedThread).map(
                          (group, groupIndex) => (
                            <div
                              key={`group-${groupIndex}-${group.items[0]?.id}`}
                              className={`lender-comm-message-row ${
                                group.isMine
                                  ? "lender-comm-message-row--mine"
                                  : "lender-comm-message-row--other"
                              }`}
                            >
                              {!group.isMine ? (
                                <ParticipantAvatar
                                  participant={group.senderParticipant}
                                  size="message"
                                />
                              ) : null}

                              <div
                                className={`lender-comm-message-group ${
                                  group.isMine
                                    ? "lender-comm-message-group--mine"
                                    : "lender-comm-message-group--other"
                                }`}
                              >
                                {group.items.map((message, itemIndex) => {
                                  const showUnreadSeparator =
                                    firstUnreadMessageId &&
                                    message.id === firstUnreadMessageId &&
                                    !group.isMine;

                                  const isFirst = itemIndex === 0;
                                  const isLast =
                                    itemIndex === group.items.length - 1;
                                  const isDeleted = Boolean(message.is_deleted);

                                  return (
                                    <React.Fragment key={message.id}>
                                      {showUnreadSeparator ? (
                                        <div className="lender-comm-unread-separator">
                                          <span className="lender-comm-unread-separator__line" />
                                          <span className="lender-comm-unread-separator__label">
                                            {t(
                                              "messages.newMessages",
                                              "New messages",
                                            )}
                                          </span>
                                          <span className="lender-comm-unread-separator__line" />
                                        </div>
                                      ) : null}

                                      <div
                                        id={`lender-comm-message-anchor-${message.id}`}
                                        className={`lender-comm-message ${
                                          group.isMine
                                            ? "lender-comm-message--mine"
                                            : "lender-comm-message--other"
                                        } ${
                                          isFirst
                                            ? "lender-comm-message--group-first"
                                            : ""
                                        } ${
                                          isLast
                                            ? "lender-comm-message--group-last"
                                            : ""
                                        } ${
                                          !isFirst && !isLast
                                            ? "lender-comm-message--group-middle"
                                            : ""
                                        } ${
                                          group.items.length === 1
                                            ? "lender-comm-message--single"
                                            : ""
                                        }`}
                                      >
                                        {isFirst ? (
                                          <div className="lender-comm-message__meta">
                                            <span className="lender-comm-message__sender">
                                              {message.sender_display ||
                                                t("common.unknown", "Unknown")}
                                            </span>
                                            <span className="lender-comm-message__type">
                                              {t(
                                                `messageTypes.${message.message_type}`,
                                                message.message_type,
                                              )}
                                            </span>
                                            <span className="lender-comm-message__time">
                                              {formatTime(
                                                message.created_at,
                                                locale,
                                              )}
                                            </span>
                                          </div>
                                        ) : null}

                                        <div
                                          className={`lender-comm-message__body ${
                                            isDeleted
                                              ? "lender-comm-message__body--deleted"
                                              : ""
                                          }`}
                                        >
                                          {isDeleted
                                            ? group.isMine
                                              ? t(
                                                  "messages.youDeletedMessage",
                                                  "You deleted this message.",
                                                )
                                              : t(
                                                  "messages.deletedMessage",
                                                  "This message was deleted.",
                                                )
                                            : message.body ||
                                              t(
                                                "common.emptyMessage",
                                                "Empty message",
                                              )}
                                        </div>

                                        {group.isMine && !isDeleted ? (
                                          <button
                                            type="button"
                                            className="lender-comm-message__delete-btn"
                                            onClick={() =>
                                              setMessageDeleteModal({
                                                open: true,
                                                messageId: message.id,
                                              })
                                            }
                                            title={t(
                                              "buttons.deleteMessage",
                                              "Delete message",
                                            )}
                                            aria-label={t(
                                              "buttons.deleteMessage",
                                              "Delete message",
                                            )}
                                          >
                                            <Trash2 size={13} />
                                          </button>
                                        ) : null}

                                        {!isFirst ? (
                                          <div className="lender-comm-message__time lender-comm-message__time--inline">
                                            {formatTime(
                                              message.created_at,
                                              locale,
                                            )}
                                          </div>
                                        ) : null}
                                      </div>
                                    </React.Fragment>
                                  );
                                })}
                              </div>
                              {group.isMine ? (
                                <ParticipantAvatar
                                  participant={group.senderParticipant}
                                  size="message"
                                />
                              ) : null}
                            </div>
                          ),
                        )
                      )}
                    </div>

                    {showScrollToBottom ? (
                      <button
                        type="button"
                        className="lender-comm-scroll-to-bottom"
                        onClick={handleScrollToUnreadOrBottom}
                      >
                        <ArrowDown size={15} />
                        {selectedThreadUnreadCount > 0
                          ? t("buttons.scrollToUnread", "Go to new messages")
                          : t("buttons.scrollToLatest", "Go to latest")}
                      </button>
                    ) : null}
                  </div>

                  <form
                    className="lender-comm-composer"
                    onSubmit={handleSendMessage}
                  >
                    <textarea
                      rows="3"
                      value={messageBody}
                      onChange={(e) => setMessageBody(e.target.value)}
                      onKeyDown={handleComposerKeyDown}
                      placeholder={t(
                        "composer.placeholder",
                        "Write your message here...",
                      )}
                    />

                    <div className="lender-comm-composer__actions">
                      <span className="lender-comm-composer__hint">
                        {t(
                          "composer.hint",
                          "Messages sent here are visible to the relevant actors in this thread.",
                        )}
                      </span>

                      <button
                        type="submit"
                        className="lender-comm-send-btn"
                        disabled={isSendingMessage || !messageBody.trim()}
                      >
                        {isSendingMessage ? (
                          <>
                            <Loader2 size={15} className="lender-comm-spin" />
                            {t("buttons.sending", "Sending...")}
                          </>
                        ) : (
                          <>
                            <Send size={15} />
                            {t("buttons.send", "Send")}
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </>
              )}
            </section>
          </div>
        </div>
      </div>

      {feedbackModal.open ? (
        <div className="lender-comm-modal-overlay" onClick={closeFeedbackModal}>
          <div
            className={`lender-comm-modal ${
              feedbackModal.type === "error"
                ? "lender-comm-modal--error"
                : "lender-comm-modal--success"
            }`}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="lender-comm-modal__icon">
              {feedbackModal.type === "error" ? (
                <AlertCircle size={22} />
              ) : (
                <CheckCircle2 size={22} />
              )}
            </div>

            <div className="lender-comm-modal__content">
              <h3>{feedbackModal.title}</h3>
              <p>{feedbackModal.text}</p>
            </div>

            <button
              type="button"
              className="lender-comm-modal__close"
              onClick={closeFeedbackModal}
              aria-label={t("common.close", "Close")}
            >
              <X size={16} />
            </button>
          </div>
        </div>
      ) : null}

      {deleteModal.open ? (
        <div
          className="lender-comm-modal-overlay"
          onClick={() =>
            setDeleteModal({
              open: false,
              threadId: null,
              title: "",
            })
          }
        >
          <div
            className="lender-comm-delete-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="lender-comm-delete-modal__icon">
              <Trash2 size={20} />
            </div>

            <div className="lender-comm-delete-modal__content">
              <h3>{t("deleteModal.title", "Delete conversation?")}</h3>
              <p>
                {t(
                  "deleteModal.text",
                  "This conversation will be permanently removed.",
                )}
              </p>
              {deleteModal.title ? (
                <div className="lender-comm-delete-modal__thread-name">
                  {deleteModal.title}
                </div>
              ) : null}
            </div>

            <div className="lender-comm-delete-modal__actions">
              <button
                type="button"
                className="lender-comm-delete-modal__cancel"
                onClick={() =>
                  setDeleteModal({
                    open: false,
                    threadId: null,
                    title: "",
                  })
                }
              >
                {t("buttons.cancel", "Cancel")}
              </button>

              <button
                type="button"
                className="lender-comm-delete-modal__confirm"
                onClick={confirmDeleteThread}
              >
                <Trash2 size={15} />
                {t("buttons.delete", "Delete")}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {messageDeleteModal.open ? (
        <div
          className="lender-comm-modal-overlay"
          onClick={() =>
            setMessageDeleteModal({
              open: false,
              messageId: null,
            })
          }
        >
          <div
            className="lender-comm-delete-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="lender-comm-delete-modal__icon">
              <Trash2 size={20} />
            </div>

            <div className="lender-comm-delete-modal__content">
              <h3>{t("messageDeleteModal.title", "Delete message?")}</h3>

              <p>
                {t(
                  "messageDeleteModal.text",
                  "The message will remain in the conversation as deleted, but its content will no longer be visible.",
                )}
              </p>
            </div>

            <div className="lender-comm-delete-modal__actions">
              <button
                type="button"
                className="lender-comm-delete-modal__cancel"
                disabled={isDeletingMessage}
                onClick={() =>
                  setMessageDeleteModal({
                    open: false,
                    messageId: null,
                  })
                }
              >
                {t("buttons.cancel", "Cancel")}
              </button>

              <button
                type="button"
                className="lender-comm-delete-modal__confirm"
                disabled={isDeletingMessage}
                onClick={confirmDeleteMessage}
              >
                {isDeletingMessage ? (
                  <>
                    <Loader2 size={15} className="lender-comm-spin" />
                    {t("buttons.deleting", "Deleting...")}
                  </>
                ) : (
                  <>
                    <Trash2 size={15} />
                    {t("buttons.delete", "Delete")}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
